import React from 'react';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { Alert, Fade } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { AccountData, PassOut } from '../../api';
import type { PassOrderPageData } from '../../api/models/PassOrderPageData';
import { customerFacingPriceFormatter } from '../../orderPage/utils';
import { withBookingFee } from '../../orderPage/core/pricing';
import { useLanguage } from '../../utils/GeneralUtils';
import { getDateFormat, getTimeFormat } from '../../utils/getDateAndTimeFormat';
import { formatSlotLabel, groupSelectionsByOrderPage } from '../../shared/passScheduleSelector/utils';
import {
  MembershipState,
  getExpirationDate,
  getMembershipState,
  getPassOrderPageName,
  getPendingSwapTarget,
  hasFailedLastCharge,
  hasPassStarted,
  isMembership,
  isPassExpired,
} from '../utils/passUtils';

type MembershipSummaryProps = {
  pass: PassOut;
  passOrderPage: PassOrderPageData | undefined;
  account: AccountData | undefined;
  variant: 'card' | 'detail';
};

/**
 * The billing line of a recurring membership: when it renews and for how much, or why it will not.
 * Punch passes render nothing here -- their expiry line stays where it is.
 *
 * A membership's `expiration_date` is the end of the paid cycle, which for a healthy membership is
 * the day it renews. Showing it as "valid until" reads like an expiry, so recurring passes get this
 * block instead of that line.
 */
export const MembershipSummary = ({ pass, passOrderPage, account, variant }: MembershipSummaryProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { longDateFormat } = getDateFormat(account);
  const timeFormat = getTimeFormat(account);

  if (!account || !isMembership(passOrderPage)) return null;

  const formatPrice = (price: number) =>
    customerFacingPriceFormatter(withBookingFee(price, account), account) as string;
  const formatDate = (date: string) => moment(date).format(longDateFormat);

  const state = getMembershipState(pass);
  const swap = pass.pending_swap;
  const expirationDate = getExpirationDate(pass, passOrderPage);

  const renderStateLine = () => {
    if (state === MembershipState.PAUSE_SCHEDULED && pass.pause_start_date) {
      // The pause cancels the direct debit when it starts, so a charge falling inside the pause
      // window will not happen -- only advertise a renewal that lands before the pause begins.
      const chargeBeforePause =
        pass.next_charge_date && moment(pass.next_charge_date).isBefore(moment(pass.pause_start_date), 'day');
      return (
        <>
          <Typography variant="body1" fontWeight={500} color="warning.main">
            {pass.pause_end_date
              ? t('manage_passes.membership.pausedFromUntil', {
                  from: formatDate(pass.pause_start_date),
                  until: formatDate(pass.pause_end_date),
                })
              : t('manage_passes.membership.pausedFrom', { from: formatDate(pass.pause_start_date) })}
          </Typography>
          {chargeBeforePause && pass.next_charge_date && (
            <Typography variant="body1" fontWeight={500} color="text.primary">
              {t('manage_passes.membership.renewsOn', {
                date: formatDate(pass.next_charge_date),
                price: formatPrice(pass.next_charge_amount ?? passOrderPage?.price ?? pass.price),
              })}
            </Typography>
          )}
        </>
      );
    }
    if (state === MembershipState.PAUSED) {
      return (
        <Typography variant="body1" fontWeight={500} color="warning.main">
          {pass.pause_end_date
            ? t('manage_passes.membership.pausedUntil', { date: formatDate(pass.pause_end_date) })
            : t('manage_passes.membership.paused')}
        </Typography>
      );
    }
    if (state === MembershipState.ENDING) {
      return (
        <Typography variant="body1" fontWeight={500} color="warning.main">
          {expirationDate
            ? t('manage_passes.membership.endsOn', { date: formatDate(expirationDate) })
            : t('manage_passes.membership.notRenewing')}
        </Typography>
      );
    }
    if (state === MembershipState.RENEWING && pass.next_charge_date) {
      // Mid-dunning `next_charge_date` is the retry day, not the renewal day -- "renews on" would read
      // as business as usual while the card is being declined, so the retry replaces that line.
      if (hasFailedLastCharge(pass)) {
        return (
          <Typography variant="body1" fontWeight={500} color="error.main">
            {t('manage_passes.membership.chargeFailedRetry', { date: formatDate(pass.next_charge_date) })}
          </Typography>
        );
      }
      const amount = pass.next_charge_amount ?? swap?.price ?? passOrderPage?.price ?? pass.price;
      return (
        <Typography variant="body1" fontWeight={500} color="text.primary">
          {t('manage_passes.membership.renewsOn', {
            date: formatDate(pass.next_charge_date),
            price: formatPrice(amount),
          })}
        </Typography>
      );
    }
    // Lapsed: no upcoming charge, and nothing that says why. A membership is a billing cycle rather
    // than a validity window, so this still talks in cycles -- never "valid until" / "days left".
    if (!expirationDate) return null;
    const ended = isPassExpired(pass, passOrderPage);
    return (
      <Typography variant="body1" fontWeight={500} color={ended ? 'error.main' : 'text.primary'}>
        {ended
          ? t('manage_passes.membership.endedOn', { date: formatDate(expirationDate) })
          : t('manage_passes.membership.cycleEndsOn', { date: formatDate(expirationDate) })}
      </Typography>
    );
  };

  const renderPendingSwap = () => {
    if (!swap) return null;
    const targetName = getPassOrderPageName(getPendingSwapTarget(account, pass), language, t);
    const effectiveDate = swap.effective_date || pass.next_charge_date;
    if (!effectiveDate) return null;

    if (variant === 'card') {
      return (
        <Typography variant="body2" color="info.main">
          {t('manage_passes.membership.pendingSwapShort', { name: targetName, date: formatDate(effectiveDate) })}
        </Typography>
      );
    }

    const scheduleGroups = groupSelectionsByOrderPage(swap.schedule_selections, account, language);
    const isMultiSchedulePage = scheduleGroups.length > 1;
    return (
      <Fade in timeout={1000}>
        <Alert severity="info" variant="outlined" sx={{ width: '100%' }}>
          <Grid container flexDirection="column" gap={1}>
            <Typography>
              {t('manage_passes.membership.pendingSwap', {
                name: targetName,
                date: formatDate(effectiveDate),
                price: formatPrice(swap.price),
              })}
            </Typography>
            {scheduleGroups.length > 0 && (
              <Grid container flexDirection="column" gap={0.75}>
                <Typography variant="body2" fontWeight={600}>
                  {t('manage_passes.membership.newSchedule', { date: formatDate(effectiveDate) })}
                </Typography>
                {scheduleGroups.map(({ opId, pageLabel, sorted }) => (
                  <Grid key={opId} container flexDirection="column" gap={0.5}>
                    {isMultiSchedulePage && pageLabel && (
                      <Typography variant="subtitle2" fontWeight={600}>
                        {pageLabel}
                      </Typography>
                    )}
                    <Grid container gap={0.75}>
                      {sorted.map((sel) => (
                        <Chip
                          key={`${sel.order_page_id}-${sel.weekday}-${sel.start_time}`}
                          size="small"
                          label={formatSlotLabel(t, sel.weekday, sel.start_time, timeFormat)}
                        />
                      ))}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Alert>
      </Fade>
    );
  };

  return (
    <Grid container flexDirection="column" gap={variant === 'card' ? 0.5 : 1}>
      {!hasPassStarted(pass) && pass.start_date && (
        <Typography variant="body1" fontWeight={500} color="warning.main">
          {t('manage_passes.pass.startsOn', { date: formatDate(pass.start_date) })}
        </Typography>
      )}
      {renderStateLine()}
      {renderPendingSwap()}
    </Grid>
  );
};
